import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../components/Badge';
import DataTable, { Column } from '../../components/DataTable';
import FormField from '../../components/FormField';
import ZKAnchorBadge, { ZKAnchorStatus } from '../../components/ZKAnchorBadge';
import { useVerificationHistory } from '../../api/hooks';
import { apiFetch } from '../../api/client';
import { encodePathSegment } from '../../lib/url';

interface AnchorBrief {
  status: ZKAnchorStatus;
  blockNumber?: number | null;
  revokedBlock?: number | null;
  txHash?: string;
}

interface DescriptorOutcomeRow {
  descriptorId: string;
  satisfied: boolean;
  credentialId?: string | null;
  format?: string | null;
  revealedFields?: string[];
  hiddenFields?: string[];
  failureReason?: string | null;
}

interface VerificationRecord {
  id: string;
  timestamp: string;
  holderDid: string;
  credentialType: string;
  issuer: string;
  result: 'verified' | 'rejected' | 'expired' | 'revoked';
  proofId?: string;
  anchor?: AnchorBrief | null;
  /**
   * Phase 7-C: present when the engine wrote per-input-descriptor outcomes
   * onto the row (`verification_history.descriptor_outcomes jsonb`). NULL on
   * pre-7-C rows; the row falls back to the flat `result` column.
   */
  descriptorOutcomes?: DescriptorOutcomeRow[] | null;
}

const resultVariant = (r: string) => {
  switch (r) {
    case 'verified': return 'success' as const;
    case 'rejected': return 'error' as const;
    case 'expired': return 'warning' as const;
    case 'revoked': return 'error' as const;
    default: return 'neutral' as const;
  }
};

export default function VerificationHistory() {
  const { data: rawHistory, loading, error } = useVerificationHistory();
  const { t } = useTranslation(['verifier', 'common']);
  const [filterResult, setFilterResult] = useState('');
  const [filterType, setFilterType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Cache anchor lookups for proofIds the server didn't pre-join.
  // 5-minute staleTime — anchors don't change often (architecture §4.8).
  const anchorCacheRef = useRef<Map<string, { value: AnchorBrief | null; fetchedAt: number }>>(new Map());
  const [anchorTick, setAnchorTick] = useState(0);
  const STALE_MS = 5 * 60 * 1000;

  const history: VerificationRecord[] = (rawHistory ?? []).map((r) => {
    const cached = r.proofId ? anchorCacheRef.current.get(r.proofId) : undefined;
    const anchor: AnchorBrief | null | undefined = r.anchor
      ? (r.anchor as AnchorBrief)
      : cached
        ? cached.value
        : undefined;
    const rawDesc = (r as { descriptorOutcomes?: unknown }).descriptorOutcomes;
    const descriptorOutcomes = Array.isArray(rawDesc) ? (rawDesc as DescriptorOutcomeRow[]) : null;
    return {
      id: r.id,
      timestamp: r.timestamp ?? r.verifiedAt ?? '',
      holderDid: r.holderDid ?? r.holder ?? '',
      credentialType: r.credentialType ?? '',
      issuer: r.issuer ?? r.issuerName ?? '',
      result: r.result as VerificationRecord['result'],
      proofId: r.proofId,
      anchor,
      descriptorOutcomes,
    };
  });

  // Per-row expand state — keyed by row id. The data already lives on the row
  // (server joins `descriptor_outcomes` into the list response), so expanding
  // does NOT trigger an extra request.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Per-row fallback: fetch anchors for proofIds the server didn't include.
  useEffect(() => {
    if (!rawHistory) return;
    const now = Date.now();
    const toFetch = rawHistory
      .filter((r) => r.proofId && !r.anchor)
      .map((r) => r.proofId as string)
      .filter((id) => {
        const cached = anchorCacheRef.current.get(id);
        return !cached || now - cached.fetchedAt > STALE_MS;
      });
    if (toFetch.length === 0) return;
    let cancelled = false;
    Promise.allSettled(
      toFetch.map((id) =>
        apiFetch<AnchorBrief>(`/zkp/proofs/${encodePathSegment(id)}/anchor`)
          .then((value) => ({ id, value }))
          .catch(() => ({ id, value: null as AnchorBrief | null })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const fetchedAt = Date.now();
      let dirty = false;
      for (const r of results) {
        if (r.status === 'fulfilled') {
          anchorCacheRef.current.set(r.value.id, { value: r.value.value, fetchedAt });
          dirty = true;
        }
      }
      if (dirty) setAnchorTick((n) => n + 1);
    });
    return () => { cancelled = true; };
    // anchorTick triggers re-render but does NOT trigger a fetch (we only care about rawHistory contents).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawHistory]);
  // Reference the tick so the lint rule treats it as observed.
  void anchorTick;

  const filtered = history.filter((r) => {
    if (filterResult && r.result !== filterResult) return false;
    if (filterType && r.credentialType !== filterType) return false;
    if (dateFrom && r.timestamp < dateFrom) return false;
    if (dateTo && r.timestamp > dateTo + ' 23:59:59') return false;
    return true;
  });

  const handleExport = () => {
    const headers = [t('history.table.timestamp'), t('history.table.holderDid'), t('history.table.credentialType'), t('history.table.issuer'), t('history.table.result')];
    const rows = filtered.map((r) => [r.timestamp, r.holderDid, r.credentialType, r.issuer, r.result]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'verification-history.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: Column<VerificationRecord>[] = [
    {
      key: 'timestamp',
      header: t('history.table.timestamp'),
      render: (row) => <span className="font-mono text-xs text-text-muted">{row.timestamp}</span>,
      sortable: true,
      sortValue: (row) => row.timestamp,
    },
    {
      key: 'holder',
      header: t('history.table.holderDid'),
      render: (row) => (
        <span className="inline-block rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-text-main">
          {row.holderDid}
        </span>
      ),
    },
    {
      key: 'type',
      header: t('history.table.credentialType'),
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary" style={{ fontSize: 16 }}>description</span>
          <span className="text-sm font-bold text-text-main">{row.credentialType}</span>
        </div>
      ),
    },
    {
      key: 'issuer',
      header: t('history.table.issuer'),
      render: (row) => <span className="text-sm text-text-main">{row.issuer}</span>,
    },
    {
      key: 'result',
      header: t('history.table.result'),
      render: (row) => <Badge variant={resultVariant(row.result)}>{row.result}</Badge>,
    },
    {
      key: 'anchor',
      header: t('history.table.anchor'),
      render: (row) => {
        if (!row.anchor) {
          // Hidden when no on-chain anchor (or no associated ZK proof).
          return <span className="text-[11px] text-text-faint">{t('history.anchor.offChain')}</span>;
        }
        return (
          <ZKAnchorBadge
            status={row.anchor.status}
            blockNumber={row.anchor.blockNumber ?? null}
            revokedBlock={row.anchor.revokedBlock ?? null}
            compact
          />
        );
      },
    },
    {
      key: 'expand',
      header: '',
      render: (row) => {
        if (!row.descriptorOutcomes || row.descriptorOutcomes.length === 0) {
          return <span className="text-[10px] text-text-faint">{t('pex.history.legacy')}</span>;
        }
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(row.id);
            }}
            className="flex items-center gap-1 text-xs font-bold text-primary hover:text-primary-dark"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
              {expanded.has(row.id) ? 'expand_less' : 'expand_more'}
            </span>
            {t('pex.history.descriptors', { count: row.descriptorOutcomes.length })}
          </button>
        );
      },
    },
  ];

  const credentialTypes = [...new Set(history.map((r) => r.credentialType))];

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('history.title')}</h1>
            <p className="mt-1 text-sm text-text-muted">{t('history.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('history.title')}</h1>
            <p className="mt-1 text-sm text-text-muted">{t('history.subtitle')}</p>
          </div>
        </div>
        <div className="text-center py-20 text-red-500">{t('common:errors.loadVerificationHistoryFailed')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('history.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('history.subtitle')}</p>
        </div>
        <button className="btn-secondary" onClick={handleExport}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span>
          {t('history.exportCsv')}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        {[
          { label: t('history.stats.total'), value: String(history.length), icon: 'fact_check' },
          { label: t('history.stats.verified'), value: String(history.filter((r) => r.result === 'verified').length), icon: 'check_circle' },
          { label: t('history.stats.rejected'), value: String(history.filter((r) => r.result === 'rejected').length), icon: 'cancel' },
          { label: t('history.stats.successRate'), value: history.length > 0 ? Math.round((history.filter((r) => r.result === 'verified').length / history.length) * 100) + '%' : '0%', icon: 'trending_up' },
        ].map((stat) => (
          <div key={stat.label} className="stat-card">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 16 }}>{stat.icon}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{stat.label}</span>
            </div>
            <span className="mt-2 block text-3xl font-bold text-text-main">{stat.value}</span>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border-subtle bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[160px]">
            <FormField
              as="select"
              label={t('history.filters.result')}
              value={filterResult}
              onChange={(e) => setFilterResult((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('history.filters.allResults')}</option>
              <option value="verified">{t('history.filters.verified')}</option>
              <option value="rejected">{t('history.filters.rejected')}</option>
              <option value="expired">{t('history.filters.expired')}</option>
              <option value="revoked">{t('history.filters.revoked')}</option>
            </FormField>
          </div>
          <div className="min-w-[200px]">
            <FormField
              as="select"
              label={t('history.filters.type')}
              value={filterType}
              onChange={(e) => setFilterType((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('history.filters.allTypes')}</option>
              {credentialTypes.map((tp) => (
                <option key={tp} value={tp}>{tp}</option>
              ))}
            </FormField>
          </div>
          <div className="min-w-[140px]">
            <FormField label={t('history.filters.dateFrom')} type="date" value={dateFrom} onChange={(e) => setDateFrom((e.target as HTMLInputElement).value)} />
          </div>
          <div className="min-w-[140px]">
            <FormField label={t('history.filters.dateTo')} type="date" value={dateTo} onChange={(e) => setDateTo((e.target as HTMLInputElement).value)} />
          </div>
          {(filterResult || filterType || dateFrom || dateTo) && (
            <button
              className="mb-[2px] text-xs font-bold text-primary hover:text-primary-dark"
              onClick={() => { setFilterResult(''); setFilterType(''); setDateFrom(''); setDateTo(''); }}
            >
              {t('history.filters.clear')}
            </button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        getId={(row) => row.id}
        pageSize={8}
        emptyMessage={t('history.table.empty')}
      />

      {/* Per-descriptor expand panels — keyed off the same row id as the table.
          The data is already on the row (server-joined), so no extra fetch. */}
      {filtered
        .filter((r) => expanded.has(r.id) && r.descriptorOutcomes && r.descriptorOutcomes.length > 0)
        .map((row) => (
          <div key={`expand-${row.id}`} className="rounded-xl border border-border-subtle bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-extrabold uppercase tracking-widest text-text-muted">
                {t('pex.history.expandTitle')} <span className="ml-1 font-mono text-text-faint">{row.id}</span>
              </div>
              <button onClick={() => toggleExpand(row.id)} className="text-xs font-bold text-primary hover:text-primary-dark">
                {t('pex.history.collapse')}
              </button>
            </div>
            <div className="space-y-2">
              {(row.descriptorOutcomes ?? []).map((d) => (
                <div key={d.descriptorId} className="rounded-lg border border-border-subtle bg-slate-50/50 p-3">
                  <div className="flex items-center gap-2">
                    <Badge variant={d.satisfied ? 'success' : 'error'}>{d.satisfied ? '✓' : '✗'}</Badge>
                    <span className="font-mono text-xs font-bold text-text-main">{d.descriptorId}</span>
                    {d.failureReason && (
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-red-600">
                        {t(`pex.failureReasons.${d.failureReason}`, { defaultValue: d.failureReason })}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-1 gap-1.5 text-[11px] md:grid-cols-2">
                    {d.credentialId && (
                      <div className="flex gap-1.5">
                        <span className="text-text-faint">{t('pex.descriptorOutcome.credential')}</span>
                        <span className="font-mono text-text-main">{d.credentialId}</span>
                      </div>
                    )}
                    {d.format && (
                      <div className="flex gap-1.5">
                        <span className="text-text-faint">{t('pex.descriptorOutcome.format')}</span>
                        <span className="font-mono text-text-main">{d.format}</span>
                      </div>
                    )}
                  </div>
                  {d.revealedFields && d.revealedFields.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-text-faint">{t('pex.descriptorOutcome.revealed')}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {d.revealedFields.map((f) => (
                          <span key={f} className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] text-blue-700">{f}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {d.hiddenFields && d.hiddenFields.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-text-faint">{t('pex.descriptorOutcome.hidden')}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {d.hiddenFields.map((f) => (
                          <span key={f} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">{f}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}
